import { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@material-ui/core';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import { FormButton } from 'components/elements';
import AlertParamsEditor from 'components/elements/AlertParamsEditor';
import { PARAMS_MODES } from 'components/elements/AlertParamsEditor/constants';
import { Title } from 'components/elements';
import CloseIcon from '@material-ui/icons/Close';
import { resetAlertPreset, saveAlertPreset } from 'redux/actions';
import { difference, isEmpty } from 'utils/helpers';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2, 2),
  },
  closeButton: {
    position: 'absolute',
    right: 8,
    top: 8,
    color: theme.palette.grey[500],
  },
}));

const SettingDialog = ({
  open,
  onClose,
  params,
  presets,
  terminalScreen,
  onChange,
  onReset,
  onSaveAsDefault,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const slot = params.presetPatternIndex;
  const savedPreset = presets[slot];

  const [mode, setMode] = useState(PARAMS_MODES.preset);
  // Preset edits stay local until saved, so the alert never silently diverges from its slot.
  const [presetDraft, setPresetDraft] = useState(savedPreset);

  useEffect(() => {
    if (open) {
      setMode(
        isEmpty(difference(savedPreset, params))
          ? PARAMS_MODES.preset
          : PARAMS_MODES.custom
      );
    }
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => {
    setPresetDraft(savedPreset);
  }, [savedPreset, open]);

  const isPresetMode = mode === PARAMS_MODES.preset;
  const isPresetDirty = isPresetMode && !_.isEqual(presetDraft, savedPreset);

  const handleModeChange = (nextMode) => {
    // Custom starts from what's on screen; going back to a preset drops the custom values.
    onReset(nextMode === PARAMS_MODES.custom ? presetDraft : savedPreset);
    setPresetDraft(savedPreset);
    setMode(nextMode);
  };

  const handleSelectPreset = (idx) => {
    if (isPresetMode) {
      onReset(presets[idx]);
    } else {
      onChange({ target: { name: 'presetPatternIndex', value: idx } });
    }
  };

  const handleChange = (e) => {
    if (isPresetMode) {
      const { name, value } = e.target;
      setPresetDraft((prev) => ({ ...prev, [name]: value }));
    } else {
      onChange(e);
    }
  };

  const handleSavePreset = async () => {
    const updated = await dispatch(saveAlertPreset(slot, presetDraft));
    if (updated) {
      onReset(updated);
    }
  };

  const handleResetPreset = async () => {
    const updated = await dispatch(resetAlertPreset(slot));
    if (updated) {
      onReset(updated);
    }
  };

  const actions = isPresetMode
    ? [
        <FormButton color="secondary" onClick={handleResetPreset}>
          Reset preset {slot} to default
        </FormButton>,
        <FormButton
          color="primary"
          disabled={!isPresetDirty}
          onClick={handleSavePreset}
        >
          Save preset {slot}
        </FormButton>,
      ]
    : [];

  actions.push(
    <FormButton
      color={isPresetMode ? 'default' : 'primary'}
      disabled={isPresetDirty}
      onClick={isPresetMode ? onSaveAsDefault : onClose}
    >
      {isPresetMode
        ? 'Use as default for new alerts'
        : 'Save custom settings for this alert'}
    </FormButton>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.root }}
    >
      <DialogTitle>
        <Title>Alert Parameters</Title>
        <IconButton className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <AlertParamsEditor
          params={isPresetMode ? presetDraft : params}
          onChange={handleChange}
          terminalScreen={terminalScreen}
          presets={presets}
          mode={mode}
          onModeChange={handleModeChange}
          selectedPreset={slot}
          onSelectPreset={handleSelectPreset}
        />
      </DialogContent>
      <DialogActions>
        <Grid container spacing={2}>
          {actions.map((action, i) => (
            <Grid item lg={12 / actions.length} xs={12} key={i}>
              {action}
            </Grid>
          ))}
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

export default SettingDialog;
